/**
 * Module Variant Service
 * Handles API operations for module variants
 */

import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { Observable, tap, catchError, throwError } from 'rxjs';
import { SIS_SCHEMA_ENDPOINTS } from '../api';
import { ModuleVariantRead } from '../models/module-variant.types';

@Injectable({
  providedIn: 'root',
})
export class ModuleVariantService {
  private readonly http = inject(HttpClient);

  // Signals for reactive state management
  readonly loading = signal(false);
  readonly error = signal<string | null>(null);

  /**
   * Get all module variants
   */
  getModuleVariants(): Observable<ModuleVariantRead[]> {
    this.loading.set(true);
    this.error.set(null);

    return this.http
      .get<ModuleVariantRead[]>(SIS_SCHEMA_ENDPOINTS.moduleVariants.list, {
        headers: this.createHeaders(),
      })
      .pipe(
        tap(() => {
          this.loading.set(false);
        }),
        catchError((error) => this.handleError(error)),
      );
  }

  /**
   * Get module variant by ID
   */
  getModuleVariant(variantId: number): Observable<ModuleVariantRead> {
    this.loading.set(true);
    this.error.set(null);

    return this.http
      .get<ModuleVariantRead>(SIS_SCHEMA_ENDPOINTS.moduleVariants.getById(variantId), {
        headers: this.createHeaders(),
      })
      .pipe(
        tap(() => {
          this.loading.set(false);
        }),
        catchError((error) => this.handleError(error)),
      );
  }

  /**
   * Create authorization headers
   */
  private createHeaders(): HttpHeaders {
    const token = this.getAuthToken();
    return new HttpHeaders({
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
    });
  }

  /**
   * Get auth token from localStorage
   */
  private getAuthToken(): string | null {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem('auth_token');
  }

  /**
   * Handle HTTP errors
   */
  private handleError(error: any): Observable<never> {
    this.loading.set(false);

    const errorMessage =
      error?.error?.message || error?.message || 'An error occurred while fetching data';

    this.error.set(errorMessage);
    console.error('ModuleVariantService error:', error);

    return throwError(() => new Error(errorMessage));
  }
}
